import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from '@startupquickstart/frontend';
import { AlertCircle } from 'lucide-react';

export interface ValidationError {
  rowNumber: number;
  field: string;
  value: string;
  message: string;
  code: 'REQUIRED' | 'INVALID_TYPE' | 'INVALID_FORMAT' | 'FK_NOT_FOUND' | 'DUPLICATE';
}

interface ValidationErrorsProps {
  errors: ValidationError[];
  maxDisplay?: number;
}

const codeColors: Record<string, string> = {
  REQUIRED: 'bg-red-100 text-red-800',
  INVALID_TYPE: 'bg-orange-100 text-orange-800',
  INVALID_FORMAT: 'bg-yellow-100 text-yellow-800',
  FK_NOT_FOUND: 'bg-blue-100 text-blue-800',
  DUPLICATE: 'bg-purple-100 text-purple-800',
};

export function ValidationErrors({ errors, maxDisplay = 100 }: ValidationErrorsProps) {
  const displayedErrors = errors.slice(0, maxDisplay);
  const hasMore = errors.length > maxDisplay;

  if (errors.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <h4 className="font-medium">
            {errors.length} validation {errors.length === 1 ? 'error' : 'errors'} found
          </h4>
        </div>
        {hasMore && (
          <Badge variant="secondary">
            Showing first {maxDisplay} of {errors.length}
          </Badge>
        )}
      </div>

      <div className="border rounded-xl max-h-[400px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              <TableHead className="w-[80px]">Row</TableHead>
              <TableHead className="w-[120px]">Field</TableHead>
              <TableHead className="w-[100px]">Type</TableHead>
              <TableHead className="w-[150px]">Value</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedErrors.map((error, index) => (
              <TableRow key={`${error.rowNumber}-${error.field}-${index}`}>
                <TableCell className="font-mono text-sm">{error.rowNumber}</TableCell>
                <TableCell>
                  <Badge variant="outline">{error.field}</Badge>
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${codeColors[error.code] || 'bg-gray-100 text-gray-800'}`}
                  >
                    {error.code}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-sm truncate max-w-[150px]" title={error.value}>
                  {error.value || '<empty>'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{error.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-sm text-muted-foreground">
        Please fix these errors in your CSV file and re-upload.
      </p>
    </div>
  );
}
